/**
 * Kebab row menu — shared by CalendarsPage and AlertsPage. Was two
 * near-identical copies; only real difference was "Copy shareable link"
 * (calendars only — alerts have no public shareable URL), now gated behind
 * the optional `onCopyLink` prop instead. Target list: Open in builder /
 * [Copy shareable link] / Duplicate — Publish|Unpublish — Delete (destructive,
 * last).
 */
import React, { useMemo } from "react";
import { useHistory } from "react-router-dom";
import DropdownMenu from "../../theme/untitled/DropdownMenu/DropdownMenu";
import IconButton from "../../theme/untitled/IconButton/IconButton";
import Icon from "../../theme/untitled/icons/Icon";
import { useCurrentOrganizationStore } from "../../../../hooks/redux/currentOrganization";
import { canPublishWidgetWithinQuota } from "../../tools";
import styles from "./RowMenu.module.scss";

const RowMenu = ({
  item,
  itemLabel,
  editUrl,
  onDuplicate,
  onCopyLink,
  onTogglePublish,
  onDelete,
  disabled,
}) => {
  const history = useHistory();
  const { currentOrganization } = useCurrentOrganizationStore();

  const isPublished = item.available === 1;
  const canPublish = isPublished || canPublishWidgetWithinQuota(currentOrganization, isPublished);

  const items = useMemo(() => {
    const menuItems = [
      {
        key: "open",
        label: "Open in builder",
        icon: <Icon name="Edit01" />,
        onClick: () => history.push(editUrl),
      },
    ];

    if (onCopyLink) {
      menuItems.push({
        key: "copy-link",
        label: "Copy shareable link",
        icon: <Icon name="Share07" />,
        disabled: !isPublished,
        onClick: () => onCopyLink(item),
      });
    }

    menuItems.push(
      {
        key: "duplicate",
        label: "Duplicate",
        icon: <Icon name="Copy01" />,
        onClick: () => onDuplicate(item),
      },
      { type: "divider" },
      {
        key: "publish",
        label: canPublish ? (
          isPublished ? "Unpublish" : "Publish"
        ) : (
          <span className={styles.publishLabel}>
            <span>Publish</span>
            <span className={styles.publishReason}>
              You&rsquo;ve reached your published-slot limit.
            </span>
          </span>
        ),
        icon: <Icon name={isPublished ? "SlashCircle01" : "CheckCircle"} />,
        disabled: !canPublish,
        onClick: () => onTogglePublish(item),
      },
      { type: "divider" },
      {
        key: "delete",
        label: `Delete ${itemLabel}`,
        icon: <Icon name="Trash01" />,
        destructive: true,
        onClick: () => onDelete(item),
      }
    );

    return menuItems;
  }, [
    history,
    editUrl,
    item,
    itemLabel,
    isPublished,
    onCopyLink,
    onDuplicate,
    canPublish,
    onTogglePublish,
    onDelete,
  ]);

  return (
    <DropdownMenu
      trigger={
        <IconButton icon={<Icon name="DotsVertical" />} label="More actions" disabled={disabled} />
      }
      items={items}
    />
  );
};

export default RowMenu;
